import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PrinterIcon } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
type FieldProps = {
  label: string;
  lines?: number;
  wide?: boolean;
};
function Field({
  label,
  lines = 1,
  wide = false
}: FieldProps) {
  return <div className={wide ? 'col-span-2' : ''} data-id="element-666">
      <p className="text-[11px] font-semibold text-slate-800 mb-1" data-id="element-667">{label}</p>
      <div className="border-b border-slate-400" style={{
      minHeight: `${lines * 25}px`
    }} data-id="element-668" />
    </div>;
}
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="break-inside-avoid mb-7" data-id="element-669">
      <h2 className="bg-[#0a1628] text-white text-[12px] font-bold tracking-wide px-3 py-2 mb-4" data-id="element-670">
        {title}
      </h2>
      {children}
    </section>;
}
function CheckboxGroup({
  items
}: {
  items: string[];
}) {
  return <div className="flex flex-wrap gap-x-5 gap-y-2 text-[11px] text-slate-800" data-id="element-671">
      {items.map(item => <span key={item} className="inline-flex items-center gap-2" data-id="element-672">
          <span className="w-3.5 h-3.5 border border-slate-600 inline-block" data-id="element-673" />
          {item}
        </span>)}
    </div>;
}
export function SupplierPreQualificationPrint() {
  useSEO({
    title: 'Supplier Pre-Qualification Form PDF',
    description: 'Download or print the 888 Sourcing Ltd Supplier Pre-Qualification and Capability Assessment Form.',
    path: '/supplier-pre-qualification-pdf'
  });
  const openPrintDialog = () => {
    window.print();
  };
  return <div className="min-h-screen bg-slate-200 py-10 px-4 print:bg-white print:p-0" data-id="element-674">
      <div className="max-w-[210mm] mx-auto mb-5 flex flex-col sm:flex-row gap-3 justify-between items-start print:hidden" data-id="element-675">
        <Link to="/supplier-registration" className="inline-flex items-center gap-2 text-sm font-semibold text-navy-dark hover:text-gold" data-id="element-676">
          <ArrowLeftIcon size={17} data-id="element-677" />
          Back to online form
        </Link>
        <div className="flex flex-wrap items-center gap-3" data-id="element-678">
          <span className="text-xs text-slate-600 max-w-sm" data-id="element-679">
            Desktop: choose “Save as PDF” in the print dialogue. Mobile: use
            your browser’s Share menu, then choose Print or Save as PDF.
          </span>
          <button type="button" onClick={openPrintDialog} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-navy-dark px-4 py-2.5 rounded-md text-sm font-semibold transition-colors" data-id="element-680">
            <PrinterIcon size={17} data-id="element-681" />
            Save / Print PDF
          </button>
        </div>
      </div>

      <main className="max-w-[210mm] min-h-[297mm] mx-auto bg-white shadow-xl print:shadow-none p-[13mm] text-slate-800 font-sans" data-id="element-682">
        <header className="border-b-2 border-[#c8924a] pb-5 mb-6" data-id="element-683">
          <div className="flex items-start justify-between gap-5" data-id="element-684">
            <div className="flex items-center gap-3" data-id="element-685">
              <div className="w-12 h-12 bg-[#c8924a] rounded-md flex items-center justify-center" data-id="element-686">
                <span className="text-[#0a1628] text-xl font-bold" data-id="element-687">888</span>
              </div>
              <div className="leading-tight" data-id="element-688">
                <p className="text-[#0a1628] text-sm font-bold tracking-[0.12em]" data-id="element-689">
                  SOURCING
                </p>
                <p className="text-[#0a1628] text-sm font-bold tracking-[0.12em]" data-id="element-690">
                  LIMITED
                </p>
              </div>
            </div>
            <div className="text-right text-[9px] leading-relaxed text-slate-600" data-id="element-691">
              <p data-id="element-692">Company No. 08400329 | VAT GB158689350</p>
              <p data-id="element-693">info@888sourcing.co.uk | +44 333 057 9291</p>
            </div>
          </div>
          <h1 className="text-[#0a1628] text-xl sm:text-2xl font-bold mt-6 leading-tight" data-id="element-694">
            SUPPLIER PRE-QUALIFICATION &amp; CAPABILITY ASSESSMENT FORM
          </h1>
          <p className="text-[11px] leading-relaxed text-slate-600 mt-3 max-w-[165mm]" data-id="element-695">
            Thank you for your interest in participating in our supplier
            evaluation and procurement sourcing programmes. Please complete all
            applicable sections and return the completed form to{' '}
            <strong data-id="element-696">info@888sourcing.co.uk</strong>.
          </p>
        </header>

        <Section title="SECTION A – COMPANY INFORMATION" data-id="element-697">
          <p className="text-[12px] font-bold mb-3" data-id="element-698">1. Company Details</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4" data-id="element-699">
            <Field label="Legal Company Name" data-id="element-700" />
            <Field label="Trading Name" data-id="element-701" />
            <Field label="Company Registration Number" data-id="element-702" />
            <Field label="VAT / Tax Registration Number" data-id="element-703" />
            <Field label="Year Established" data-id="element-704" />
            <Field label="Country of Registration" data-id="element-705" />
            <Field label="Registered Address" lines={3} wide data-id="element-706" />
            <Field label="Website" wide data-id="element-707" />
            <Field label="Main Contact Person" data-id="element-708" />
            <Field label="Position" data-id="element-709" />
            <Field label="Email Address" data-id="element-710" />
            <Field label="Telephone Number" data-id="element-711" />
          </div>
        </Section>

        <Section title="SECTION B – BUSINESS PROFILE" data-id="element-712">
          <p className="text-[12px] font-bold mb-2" data-id="element-713">2. Business Activities</p>
          <p className="text-[10px] text-slate-600 mb-1" data-id="element-714">
            Please describe your company's primary business activities.
          </p>
          <Field label="" lines={3} wide data-id="element-715" />
          <p className="text-[12px] font-bold mt-5 mb-2" data-id="element-716">
            3. Product Categories
          </p>
          <p className="text-[10px] text-slate-600 mb-1" data-id="element-717">
            Please list your primary product categories.
          </p>
          <Field label="" lines={3} wide data-id="element-718" />
          <p className="text-[12px] font-bold mt-5 mb-2" data-id="element-719">
            4. Manufacturing / Supply Structure
          </p>
          <CheckboxGroup items={['Manufacturer', 'Brand Owner', 'Exporter', 'Distributor', 'OEM', 'Other']} data-id="element-720" />
          <p className="text-[12px] font-bold mt-5 mb-2" data-id="element-721">
            5. Production Capacity
          </p>
          <p className="text-[10px] text-slate-600 mb-1" data-id="element-722">
            Please provide estimated monthly production capacity.
          </p>
          <Field label="" lines={2} wide data-id="element-723" />
        </Section>

        <Section title="SECTION C – EXPORT & INTERNATIONAL EXPERIENCE" data-id="element-724">
          <p className="text-[12px] font-bold mb-2" data-id="element-725">
            6. Current Export Markets
          </p>
          <Field label="" lines={2} wide data-id="element-726" />
          <p className="text-[12px] font-bold mt-5 mb-2" data-id="element-727">
            7. Existing Retail / Institutional Clients
          </p>
          <Field label="" lines={2} wide data-id="element-728" />
          <p className="text-[12px] font-bold mt-5 mb-2" data-id="element-729">
            8. Export Documentation Capability
          </p>
          <p className="text-[10px] text-slate-600 mb-1" data-id="element-730">
            Describe your capability to provide export documentation (e.g.
            Certificate of Origin, Commercial Invoice, Packing List, etc.).
          </p>
          <Field label="" lines={3} wide data-id="element-731" />
        </Section>

        <Section title="SECTION D – PRODUCT & QUALITY INFORMATION" data-id="element-732">
          <p className="text-[12px] font-bold mb-2" data-id="element-733">9. Certifications</p>
          <CheckboxGroup items={['ISO', 'HACCP', 'BRC', 'CE', 'FDA', 'Organic', 'Halal', 'Other']} data-id="element-734" />
          <div className="mt-3" data-id="element-735">
            <Field label="Other certifications (please specify)" data-id="element-736" />
          </div>
          <p className="text-[12px] font-bold mt-5 mb-2" data-id="element-737">
            10. Quality Control Procedures
          </p>
          <Field label="" lines={3} wide data-id="element-738" />
        </Section>

        <Section title="SECTION E – COMMERCIAL & LOGISTICS INFORMATION" data-id="element-739">
          <p className="text-[12px] font-bold mb-2" data-id="element-740">
            11. Pricing Terms Available
          </p>
          <CheckboxGroup items={['EXW', 'FOB', 'CIF', 'DAP', 'DDP']} data-id="element-741" />
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 mt-5" data-id="element-742">
            <Field label="12. Standard Lead Time" data-id="element-743" />
            <Field label="13. Minimum Order Quantity (MOQ)" data-id="element-744" />
            <Field label="14. Packaging Capabilities" lines={3} wide data-id="element-745" />
            <Field label="15. Logistics & Shipping Capability" lines={3} wide data-id="element-746" />
          </div>
        </Section>

        <Section title="SECTION F – COMPLIANCE & DECLARATION" data-id="element-747">
          <p className="text-[11px] leading-relaxed italic mb-5" data-id="element-748">
            We hereby confirm that the information provided is accurate to the
            best of our knowledge.
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4" data-id="element-749">
            <Field label="Authorised Representative (Full Name)" data-id="element-750" />
            <Field label="Position" data-id="element-751" />
            <Field label="Signature" data-id="element-752" />
            <Field label="Date" data-id="element-753" />
          </div>
          <div className="mt-6 space-y-3" data-id="element-754">
            <CheckboxGroup items={['I confirm that the information provided is accurate to the best of my knowledge.', 'I consent to 888 Sourcing Ltd processing this information in accordance with the Privacy Policy.', "I would like to receive procurement updates and opportunity alerts relevant to my company's capabilities."]} data-id="element-755" />
          </div>
        </Section>

        <footer className="mt-8 pt-4 border-t border-slate-300 text-[10px] text-slate-600" data-id="element-756">
          <p className="font-bold text-[#0a1628]" data-id="element-757">
            Return completed forms to: info@888sourcing.co.uk
          </p>
          <p className="mt-1" data-id="element-758">
            Please do not include supporting documents unless requested by our
            procurement team.
          </p>
        </footer>
      </main>
    </div>;
}